// src/pages/Feedback/FeedbackFormMentor.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import MatchDecisionPanel from '../../components/MatchDecisionPanel';

const MENTIXX_GREEN = '#00ff88';

const QUESTIONS = [
  {
    key: 'Engagement',
    label:
      'Wie beurteilen Sie das Engagement Ihres Mentees im Mentoring-Prozess insgesamt?'
  },
  {
    key: 'Kommunikation',
    label:
      'Wie zufrieden sind Sie mit der Kommunikation (Verbindlichkeit, Reaktionszeit, Zuverlässigkeit) Ihres Mentees?'
  },
  {
    key: 'Vertrauen',
    label:
      'Wie würden Sie das Vertrauensverhältnis zwischen Ihnen und Ihrem Mentee einschätzen?'
  },
  {
    key: 'Reflexion',
    label:
      'Wie gut reflektiert Ihr Mentee aus Ihrer Sicht die eigenen Ziele, Stärken und Entwicklungsfelder?'
  }
];

function isFormComplete(values) {
  return QUESTIONS.every((q) => values[q.key]) && values.satisfaction_score;
}

function getSatisfactionLabel(value, t) {
  const v = Number(value);
  if (!v) return t('feedback.satisfaction.unknown') || 'Keine Angabe';
  if (v === 1)
    return t('feedback.satisfaction.veryDissatisfied') || 'Sehr unzufrieden';
  if (v === 2) return t('feedback.satisfaction.dissatisfied') || 'Eher unzufrieden';
  if (v === 3) return t('feedback.satisfaction.neutral') || 'Neutral';
  if (v === 4) return t('feedback.satisfaction.satisfied') || 'Eher zufrieden';
  if (v === 5)
    return t('feedback.satisfaction.verySatisfied') || 'Sehr zufrieden';
  return t('feedback.satisfaction.unknown') || 'Keine Angabe';
}

function getBadgeBackground(value) {
  const v = Number(value);
  if (!v) return 'bg-gray-700';
  if (v <= 2) return 'bg-red-600';
  if (v === 3) return 'bg-yellow-500';
  return 'bg-green-500';
}

function getBadgeTextColor(value) {
  const v = Number(value);
  if (!v) return 'text-gray-300';
  if (v === 3) return 'text-gray-900';
  return 'text-white';
}

export default function FeedbackFormMentor() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [values, setValues] = useState({
    Engagement: '',
    Kommunikation: '',
    Vertrauen: '',
    Reflexion: '',
    comments: '',
    satisfaction_score: ''
  });

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [pairInfo, setPairInfo] = useState(null);
  const [matchMeta, setMatchMeta] = useState(null);

  const uid = searchParams.get('uid');
  const milestone = searchParams.get('milestone') || 'kennenlerntermin';
  const role = searchParams.get('role') || 'mentor';

  // NEU: mentee_id aus URL holen und in Zahl umwandeln
  const menteeIdRaw = searchParams.get('mentee_id');
  const menteeId =
    menteeIdRaw !== null && menteeIdRaw !== '' ? Number(menteeIdRaw) : null;

  const [pairLoading, setPairLoading] = useState(true);
  const [pairError, setPairError] = useState('');

  const canSubmit = useMemo(() => isFormComplete(values), [values]);

  useEffect(() => {
    let isMounted = true;

    const loadPairInfo = async () => {
      if (!uid || !milestone) {
        setPairError('Feedback-Link ist unvollständig.');
        setPairLoading(false);
        return;
      }

      // NEU: Validierung für Mentor-Links – mentee_id muss Zahl sein
      if (role === 'mentor' && !Number.isFinite(menteeId)) {
        setPairError(
          'mentee_id ist für role=mentor erforderlich und muss eine Zahl sein'
        );
        setPairLoading(false);
        return;
      }

      try {
        const res = await axios.get('/mentoring/feedback/link-info', {
          params: {
            uid,
            milestone,
            role,
            // NEU: mentee_id nur senden, wenn Mentor und gültig
            ...(role === 'mentor' && Number.isFinite(menteeId)
              ? { mentee_id: menteeId }
              : {})
          },
          withCredentials: true
        });

        if (!isMounted) return;

        const data = res.data || {};
        if (!data.mentee || !data.mentor) {
          setPairError(
            'Zu diesem Link konnte kein Mentoring-Paar gefunden werden.'
          );
        } else {
          setPairInfo({
            mentee: data.mentee,
            mentor: data.mentor,
            tenantName: data.tenant_name || null,
            surveyTitle: data.survey_title || null
          });
          setMatchMeta(data.matchMeta || null);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Fehler beim Laden von link-info:', err);
        setPairError(
          err?.response?.data?.error ||
            'Fehler beim Laden der Mentoring-Paar-Informationen.'
        );
      } finally {
        if (isMounted) setPairLoading(false);
      }
    };

    loadPairInfo();
    return () => {
      isMounted = false;
    };
  }, [uid, milestone, role, menteeId]); // NEU: menteeId als Dependency

  const handleChange = (key, value) => {
    setValues((prev) => ({
      ...prev,
      [key]: value
    }));
    setSubmitError('');
    setSubmitSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);

    if (!uid) {
      setSubmitError('Ungültiger Link: UID fehlt.');
      return;
    }

    if (!canSubmit) {
      setSubmitError(
        'Bitte bewerten Sie alle Kriterien und geben Sie Ihre Gesamtzufriedenheit an.'
      );
      return;
    }

    setLoading(true);

    try {
      const payloadCriteria = {};
      for (const { key } of QUESTIONS) {
        const val = values[key];
        if (val) {
          payloadCriteria[key] = Number(val);
        }
      }

      const res = await axios.post(
        '/mentoring/assessment',
        {
          user_id: pairInfo?.mentor?.user_id,
          role: 'mentor',
          milestone,
          source: 'reminder',
          target_user_id: pairInfo?.mentee?.user_id,
          criteria: payloadCriteria,
          comments: values.comments || '',
          satisfaction_score: values.satisfaction_score
            ? Number(values.satisfaction_score)
            : null,
          uid
        },
        { withCredentials: true }
      );

      if (res.data && res.data.success) {
        setSubmitSuccess(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitError(
          res.data?.error ||
            'Das Feedback konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.'
        );
      }
    } catch (err) {
      console.error('Fehler beim Absenden des Mentor-Feedbacks:', err);
      setSubmitError(
        err?.response?.data?.error ||
          'Beim Speichern ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.'
      );
    } finally {
      setLoading(false);
    }
  };

  const title = useMemo(() => {
    if (!pairInfo) {
      return (
        t('feedback.mentor.title') || 'Feedback zum Mentoring (Mentorensicht)'
      );
    }
    const menteeName = `${pairInfo.mentee.first_name || ''} ${
      pairInfo.mentee.last_name || ''
    }`.trim();
    return (
      `${t('feedback.mentor.titleShort') || 'Mentor-Feedback'} – ${menteeName}`
    );
  }, [pairInfo, t]);

  const satisfactionLabel = useMemo(
    () => getSatisfactionLabel(values.satisfaction_score, t),
    [values.satisfaction_score, t]
  );

  if (pairLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-4 border-gray-600 border-t-[3px] border-t-[#8FFFBA] rounded-full animate-spin mx-auto" />
          <p className="text-sm text-gray-300">
            {t('feedback.loadingPair') ||
              'Lade die Informationen zu Ihrem Mentoring-Paar…'}
          </p>
        </div>
      </div>
    );
  }

  if (pairError) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white px-4">
        <div className="max-w-lg w-full bg-gray-800 rounded-2xl p-6 shadow-lg border border-red-500/40">
          <h1 className="text-xl font-bold mb-2 text-red-400">
            {t('feedback.linkErrorTitle') || 'Feedback-Link nicht gültig'}
          </h1>
          <p className="text-sm text-gray-200 mb-4">{pairError}</p>
          <p className="text-xs text-gray-400">
            {t('feedback.linkErrorHint') ||
              'Bitte wenden Sie sich an Ihr HR-Team oder den Programmverantwortlichen, wenn Sie Unterstützung benötigen.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white flex justify-center px-4 py-10">
      <div className="w-full max-w-5xl space-y-8">
        <header className="space-y-3">
          <p className="text-xs text-emerald-300 uppercase tracking-[0.2em]">
            {t('feedback.programLabel') ||
              'MENVIO Mentoring · Mentor-Feedback'}
          </p>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            {title}
          </h1>

          {pairInfo && (
            <div className="flex flex-wrap gap-3 text-xs text-gray-300">
              {pairInfo.mentee && (
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-800/80 border border-gray-700/60">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  {t('feedback.menteeLabel') || 'Mentee'}:{' '}
                  <span className="font-semibold ml-1">
                    {pairInfo.mentee.first_name} {pairInfo.mentee.last_name}
                  </span>
                </span>
              )}
              {pairInfo.mentor && (
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-800/80 border border-gray-700/60">
                  <span className="w-2 h-2 rounded-full bg-sky-400" />
                  {t('feedback.mentorLabel') || 'Mentor'}:{' '}
                  <span className="font-semibold ml-1">
                    {pairInfo.mentor.first_name} {pairInfo.mentor.last_name}
                  </span>
                </span>
              )}
              {pairInfo.tenantName && (
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-800/80 border border-gray-700/60">
                  <span className="w-2 h-2 rounded-full bg-purple-400" />
                  {pairInfo.tenantName}
                </span>
              )}
              {pairInfo.surveyTitle && (
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-800/80 border border-gray-700/60">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  {pairInfo.surveyTitle}
                </span>
              )}
            </div>
          )}
        </header>

        {matchMeta && (
          <MatchDecisionPanel
            mentee={pairInfo?.mentee || null}
            mentor={pairInfo?.mentor || null}
            matchMeta={matchMeta}
            compact
          />
        )}

        <form
          onSubmit={handleSubmit}
          className="bg-gray-900/80 border border-gray-800 rounded-2xl shadow-xl p-6 sm:p-8 space-y-8"
        >
          <section className="space-y-4">
            <h2 className="text-lg font-semibold">
              {t('feedback.mentor.sectionEngagementTitle') ||
                'Einschätzung des Engagements Ihres Mentees'}
            </h2>
            <p className="text-xs text-gray-300 max-w-2xl">
              {t('feedback.mentor.sectionEngagementDesc') ||
                'Bitte bewerten Sie die folgenden Kriterien auf einer Skala von 1 (sehr gering) bis 5 (sehr hoch).'}
            </p>

            <div className="space-y-4">
              {QUESTIONS.map(({ key, label }) => (
                <div
                  key={key}
                  className="bg-gray-900 border border-gray-800 rounded-xl p-4 sm:p-5 space-y-3"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <p className="text-sm text-gray-100">{label}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span>{t('feedback.scaleLabel') || 'Skala'}:</span>
                      <span className="inline-flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-red-500" /> 1
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-yellow-500" /> 3
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-green-500" /> 5
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-1">
                    {[1, 2, 3, 4, 5].map((v) => {
                      const isSelected = Number(values[key]) === v;
                      let bg = 'bg-gray-800';
                      let border = 'border-gray-700';
                      if (v <= 2) {
                        bg = 'bg-red-900/40';
                        border = 'border-red-600/60';
                      } else if (v === 3) {
                        bg = 'bg-yellow-900/40';
                        border = 'border-yellow-500/60';
                      } else {
                        bg = 'bg-green-900/30';
                        border = 'border-emerald-500/60';
                      }

                      return (
                        <button
                          key={v}
                          type="button"
                          onClick={() => handleChange(key, v)}
                          className={`w-10 h-10 rounded-lg border flex items-center justify-center text-sm font-semibold transition
                            ${bg} ${border}
                            ${
                              isSelected
                                ? 'ring-2 ring-offset-2 ring-offset-gray-900 ring-emerald-400'
                                : 'opacity-80 hover:opacity-100'
                            }`}
                        >
                          {v}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h2 className="text-lg font-semibold">
                {t('feedback.mentor.sectionSatisfactionTitle') ||
                  'Ihre Gesamtzufriedenheit mit dem Mentoring'}
              </h2>
              {values.satisfaction_score && (
                <div
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getBadgeBackground(
                    values.satisfaction_score
                  )} ${getBadgeTextColor(values.satisfaction_score)}`}
                >
                  {t('feedback.currentSatisfaction') ||
                    'Aktuelle Einschätzung'}
                  :{' '}
                  <span className="ml-1 font-semibold">
                    {satisfactionLabel}
                  </span>
                </div>
              )}
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 sm:p-5 space-y-3">
              <p className="text-sm text-gray-100">
                {t('feedback.mentor.satisfactionQuestion') ||
                  'Wie zufrieden sind Sie insgesamt mit dem Verlauf des Mentorings und der Zusammenarbeit mit Ihrem Mentee?'}
              </p>

              <div className="flex flex-wrap gap-2 mt-1">
                {[1, 2, 3, 4, 5].map((v) => {
                  const isSelected = Number(values.satisfaction_score) === v;
                  let bg = 'bg-gray-800';
                  let border = 'border-gray-700';
                  if (v <= 2) {
                    bg = 'bg-red-900/40';
                    border = 'border-red-600/60';
                  } else if (v === 3) {
                    bg = 'bg-yellow-900/40';
                    border = 'border-yellow-500/60';
                  } else {
                    bg = 'bg-green-900/30';
                    border = 'border-emerald-500/60';
                  }

                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => handleChange('satisfaction_score', v)}
                      className={`w-10 h-10 rounded-lg border flex items-center justify-center text-sm font-semibold transition
                        ${bg} ${border}
                        ${
                          isSelected
                            ? 'ring-2 ring-offset-2 ring-offset-gray-900 ring-emerald-400'
                            : 'opacity-80 hover:opacity-100'
                        }`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">
              {t('feedback.mentor.sectionCommentsTitle') ||
                'Offenes Feedback & Hinweise'}
            </h2>
            <p className="text-xs text-gray-300 max-w-2xl">
              {t('feedback.mentor.sectionCommentsDesc') ||
                'Gibt es etwas, das Sie zum bisherigen Prozess, zur Zusammenarbeit mit Ihrem Mentee oder zu unserem Programm ergänzen möchten?'}
            </p>

            <textarea
              className="w-full min-h-[120px] bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
              placeholder={
                t('feedback.mentor.commentsPlaceholder') ||
                'Optional: z. B. besondere Fortschritte, Herausforderungen, organisatorische Hinweise …'
              }
              value={values.comments}
              onChange={(e) => handleChange('comments', e.target.value)}
            />
          </section>

          {submitError && (
            <div className="bg-red-900/40 border border-red-500/70 text-red-100 text-xs rounded-xl px-3 py-2">
              {submitError}
            </div>
          )}

          {submitSuccess && (
            <div className="bg-emerald-900/30 border border-emerald-500/80 text-emerald-100 text-xs rounded-xl px-3 py-2">
              {t('feedback.mentor.successMessage') ||
                'Vielen Dank! Ihr Feedback wurde erfolgreich gespeichert.'}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <p className="text-[11px] text-gray-400 max-w-xs">
              {t('feedback.mentor.privacyHint') ||
                'Ihr Feedback wird vertraulich behandelt und ausschließlich für die Weiterentwicklung des Mentorings genutzt.'}
            </p>

            <button
              type="submit"
              disabled={loading || !canSubmit}
              className={`inline-flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-md transition
                ${
                  loading || !canSubmit
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-emerald-500 hover:bg-emerald-400 text-gray-900'
                }`}
            >
              {loading
                ? t('feedback.saving') || 'Speichere…'
                : t('feedback.submit') || 'Feedback absenden'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
